class CartUpsell {
  constructor() {
    this.currency  = window.Shopify?.currency?.active || 'USD';
    this.isLoading = false;
    this.timer     = null;
    this.bindEvents();
    this.render(); // run on page load (catches cart page)
  }

  bindEvents() {
    // Dawn dispatches 'cart:open' when the drawer opens
    document.addEventListener('cart:open', () => this.render());
    document.addEventListener('cart:opened', () => this.render());

    // Watch for Dawn's cart-drawer custom element opening
    const observer = new MutationObserver(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.hasAttribute('open')) this.render();
    });

    const cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      observer.observe(cartDrawerEl, { attributes: true, attributeFilter: ['open', 'class'] });
    }

    // Watch for DOM changes in case drawer is injected after page load
    const bodyObserver = new MutationObserver(() => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        const wrapper = document.getElementById('cartUpsell');
        const inner   = document.getElementById('cartUpsellInner');
        if (wrapper && inner && inner.innerHTML.trim() === '' && !this.isLoading) {
          this.render();
        }
      }, 300);
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  async render() {
    if (this.isLoading) return;
    this.isLoading = true;

    const wrapper = document.getElementById('cartUpsell');
    const inner   = document.getElementById('cartUpsellInner');
    if (!wrapper || !inner) { this.isLoading = false; return; }

    this.showSkeleton(wrapper, inner);

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      if (!cart.items?.length) return this.hide(wrapper, inner);

      const anchor       = cart.items[0];
      const cartIds      = cart.items.map(i => i.product_id);
      const cartTypes    = cart.items.map(i => (i.product_type || '').toLowerCase());
      const recs         = await this.fetchRecs(anchor.product_id);

      if (!recs?.length) return this.hide(wrapper, inner);

      const eligible     = recs.filter(p => !cartIds.includes(p.id));
      if (!eligible.length) return this.hide(wrapper, inner);

      // Prefer a product type NOT already in the cart (complementary)
      const pick = eligible.find(p => !cartTypes.includes((p.product_type || '').toLowerCase()))
                   || eligible[0];

      this.renderProduct(pick, wrapper, inner);

    } catch (e) {
      console.warn('[CartUpsell]', e.message);
      const w = document.getElementById('cartUpsell');
      const i = document.getElementById('cartUpsellInner');
      if (w && i) this.hide(w, i);
    } finally {
      this.isLoading = false;
    }
  }

  async fetchRecs(productId) {
    const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=8&intent=related`);
    if (!res.ok) throw new Error('Recs fetch failed');
    return (await res.json()).products;
  }

  renderProduct(product, wrapper, inner) {
    const variant  = product.variants[0];
    const price    = this.money(variant.price);
    const compare  = variant.compare_at_price > variant.price ? this.money(variant.compare_at_price) : null;
    const img      = product.featured_image ? `${product.featured_image.split('?')[0]}?width=130` : '';

    inner.innerHTML = `
      <span class="cart-upsell__label">You might also like</span>
      <div class="cart-upsell__card">
        <a href="/products/${product.handle}" class="cart-upsell__image-wrap" tabindex="-1" aria-hidden="true">
          ${img ? `<img src="${img}" alt="${this.esc(product.title)}" class="cart-upsell__image" width="60" height="60" loading="lazy">` : ''}
        </a>
        <div class="cart-upsell__details">
          ${product.product_type ? `<span class="cart-upsell__type">${this.esc(product.product_type)}</span>` : ''}
          <a href="/products/${product.handle}" class="cart-upsell__title">${this.esc(product.title)}</a>
          <div class="cart-upsell__pricing">
            <span class="cart-upsell__price">${price}</span>
            ${compare ? `<s class="cart-upsell__compare-price">${compare}</s>` : ''}
          </div>
        </div>
        <button class="cart-upsell__add-btn" data-variant-id="${variant.id}" aria-label="Add ${this.esc(product.title)} to cart">Add</button>
      </div>
    `;

    wrapper.style.display = 'block';
    inner.querySelector('.cart-upsell__add-btn')
      ?.addEventListener('click', e => this.addToCart(variant.id, e.currentTarget));
  }

  async addToCart(variantId, btn) {
    if (btn.disabled) return;
    btn.disabled    = true;
    btn.textContent = '···';

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!res.ok) throw new Error('Add failed');

      btn.classList.add('cart-upsell__add-btn--success');
      btn.textContent = '✓ Added';

      // Update cart count bubble (Dawn's selector)
      const cart  = await fetch('/cart.js').then(r => r.json());
      document.querySelectorAll('#cart-icon-bubble .icon-count, [data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Notify Dawn to refresh its cart drawer contents
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      // Re-render upsell after a moment with a fresh suggestion
      setTimeout(() => { this.isLoading = false; this.render(); }, 1400);

    } catch (e) {
      console.warn('[CartUpsell] Add failed:', e.message);
      btn.disabled    = false;
      btn.textContent = 'Add';
    }
  }

  showSkeleton(wrapper, inner) {
    wrapper.style.display = 'block';
    inner.innerHTML = `
      <div class="cart-upsell__skeleton">
        <div class="cart-upsell__skeleton-img"></div>
        <div class="cart-upsell__skeleton-text">
          <div class="cart-upsell__skeleton-line"></div>
          <div class="cart-upsell__skeleton-line"></div>
        </div>
      </div>`;
  }

  hide(wrapper, inner) {
    wrapper.style.display = 'none';
    inner.innerHTML = '';
  }

  money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, minimumFractionDigits: 2 }).format(cents / 100);
  }

  esc(str) {
    return String(str).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m]));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartUpsell());
} else {
  new CartUpsell();
}